'use client'

import { useCallback } from 'react'

import { type Breakpoint, useBreakpoint } from './use-breakpoint.ts'

type ResponsiveOverrides<T> = Partial<Record<Breakpoint, T>>

/**
 * Returns a helper function to resolve responsive values based on the current breakpoint.
 * Think of this like using the Tailwind's responsive prefixes sm:md:lg:xl:2xl classes but within javascript.
 *
 * @example
 * const responsive = useResponsive()
 * <Widget size={responsive('small', { lg: 'large' })} />
 *
 * would be like doing <div className="base-case lg:large-case" />
 */
function useResponsive() {
  const breakpoints = useBreakpoint()

  return useCallback(
    <T>(base: T, overrides?: ResponsiveOverrides<T>): T => {
      if (!overrides) {
        return base
      }

      if (breakpoints['2xl'] && overrides['2xl'] !== undefined) {
        return overrides['2xl']
      }

      if (breakpoints.xl && overrides.xl !== undefined) {
        return overrides.xl
      }

      if (breakpoints.lg && overrides.lg !== undefined) {
        return overrides.lg
      }

      if (breakpoints.md && overrides.md !== undefined) {
        return overrides.md
      }

      if (breakpoints.sm && overrides.sm !== undefined) {
        return overrides.sm
      }

      return base
    },
    [breakpoints]
  )
}

export { type ResponsiveOverrides, useResponsive }
